import {
  AfterViewInit, Component, ElementRef, OnInit, Renderer2, ViewChild
} from '@angular/core';
import {
  FormControl, FormGroup, ReactiveFormsModule, Validators
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { takeUntil } from 'rxjs';

import {
  FilterSelectModule, IOptionCategory
} from '@sondermind/features/filter-form';
import { IState, StatesServedStore } from '@sondermind/data-access/public-facing/states-served';
import { DropdownOption, NavigationKeys } from '@sondermind/iris/dropdown';
import { IrisModalRef } from '@sondermind/iris/modal';
import { BreakpointsService } from '@sondermind/utilities/services';
import { GtmDataLayerService } from '@sondermind/google-tag-manager';

interface IStateForm {
  state: FormControl<string | null>;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FilterSelectModule
  ],
  selector: 'location-needed',
  templateUrl: './location-needed.component.html',
  styleUrls: ['./location-needed.component.scss'],
})
export class LocationNeededComponent implements OnInit, AfterViewInit {
  stateOptions: Array<IOptionCategory<string>> = [];
  options: Array<DropdownOption<string>> = [];
  stateForm: FormGroup<IStateForm> = new FormGroup({
    state: new FormControl<string | null>(null, Validators.required),
  });
  styleTag: HTMLStyleElement;
  isMobile: boolean = false;

  @ViewChild('listbox') listbox: ElementRef;
  stateHtmlElements: HTMLElement[] = [];

  currentFocusedValue: string = '';
  // This component will allow for mixed arrow key and tab navigation
  // To support them together and keep the focused element consistent,
  // we need to track Shift+Tab and track Shift separately as a held key
  isShiftBeingPressed: boolean = false;

  constructor(
    private irisModalRef: IrisModalRef<LocationNeededComponent, null, string>,
    public breakpointService: BreakpointsService,
    private renderer: Renderer2,
    private el: ElementRef,
    private gtmDataLayerService: GtmDataLayerService,
    private statesServedStore: StatesServedStore
  ) { }

  ngOnInit(): void {
    this.breakpointService.isMobile$.subscribe((val) => {
      this.isMobile = val;
      if (!val) {
        this.injectModalStyles();
      }
    });

    this.getStateOptions();

    this.stateForm.valueChanges.subscribe(({ state }) => {
      if (state) {
        this.gtmDataLayerService.sendProviderDirectoryInitialState(state, 'modal');
        this.close();
      }
    });
  }

  ngAfterViewInit(): void {
    this.createOptionsHtmlElementsList();
  }

  private createOptionsHtmlElementsList(): void {
    if (this.listbox) {
      const nodes = this.listbox.nativeElement.childNodes as NodeList;
      nodes.forEach((node: Node) => {
        const element = node as HTMLElement;
        // the header and any comments will not have aria selected values
        if (element.ariaSelected) {
          this.stateHtmlElements.push(element);
        }
      });

      this.focusListItem(this.stateHtmlElements[0]);
      this.currentFocusedValue = this.options[0].value;
    }
  }

  close(): void {
    this.styleTag?.parentNode.removeChild(this.styleTag);
    this.irisModalRef.close(this.stateForm.controls.state.value);
  }

  onMobileStateSelectKeyDown(e: KeyboardEvent, state: string): void {
    e.preventDefault();
    switch (e.key) {
      case NavigationKeys.Enter:
      case NavigationKeys[' ']:
        this.updateStateMobile(state);
        break;
      case 'Shift':
        this.isShiftBeingPressed = true;
        break;
      case NavigationKeys.ArrowUp:
        this.navigateList(NavigationKeys.ArrowUp);
        break;
      case NavigationKeys.ArrowDown:
        this.navigateList(NavigationKeys.ArrowDown);
        break;
      case NavigationKeys.Tab:
        this.navigateList(NavigationKeys.Tab);
        break;
      case NavigationKeys.Home:
        this.currentFocusedValue = this.options[0].value;
        this.focusListItem(this.stateHtmlElements[0]);
        break;
      case NavigationKeys.End:
        this.currentFocusedValue = this.options[this.options.length - 1].value;
        this.focusListItem(this.stateHtmlElements[this.stateHtmlElements.length - 1]);
        break;
      default:
        // eslint-disable-next-line no-useless-return
        return;
    }
  }

  onMobileStateSelectKeyUp(e: KeyboardEvent): void {
    // The only key up event we care about is Shift being released
    if (e.key === 'Shift') {
      this.isShiftBeingPressed = false;
    }
  }

  updateStateMobile(state: string): void {
    this.stateForm.controls.state.setValue(state);
  }

  private getStateOptions(): void {
    this.statesServedStore.stateList$().pipe(
      takeUntil(this.irisModalRef.afterClosed$()),
    ).subscribe((array: IState[]) => {
      this.stateOptions = [{
        name: 'My location',
        options: array.map(
          (arrayMember: IState) => (
            { value: arrayMember.abbreviation, name: arrayMember.name }
          )
        )
      }];
      this.options = this.stateOptions[0].options;
    });
  }

  private injectModalStyles(): void {
    // due to scoping issues with other standalone components in this project,
    // we use renderer to safely scope necessary styles only to this modal
    const css = `
      .cdk-overlay-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5) !important;
        opacity: 1 !important;
        pointer-events: none;
      }
      .cdk-overlay-pane {
        position: fixed !important;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
      .cdk-overlay-container {
        width: unset; 
      }
      @media (min-width: 960px) {
        .cdk-overlay-pane {
          position: fixed;
          top: 40%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 1050;
        }
      }
    }`;

    this.styleTag = this.renderer.createElement('style');
    this.renderer.appendChild(this.styleTag, this.renderer.createText(css));
    this.renderer.appendChild(this.el.nativeElement, this.styleTag);
  }

  private navigateList(direction: NavigationKeys.ArrowDown | NavigationKeys.ArrowUp | NavigationKeys.Tab): void {
    const currentItemIndex: number = this.options.findIndex((option) => option.value === this.currentFocusedValue);

    let nextItemIndex: number = -1;
    let isEndOfList = true;

    if (direction === NavigationKeys.ArrowDown && currentItemIndex < this.options.length - 1) {
      isEndOfList = false;
      nextItemIndex = currentItemIndex + 1;
    } else if (direction === NavigationKeys.ArrowUp && currentItemIndex > 0) {
      isEndOfList = false;
      nextItemIndex = currentItemIndex - 1;
    } else if (direction === NavigationKeys.Tab) {
      isEndOfList = false;
      // Tab should allow the navigation to wrap around
      if (this.isShiftBeingPressed) {
        nextItemIndex = currentItemIndex === 0 ? this.options.length - 1 : currentItemIndex - 1;
      } else {
        nextItemIndex = currentItemIndex === this.options.length - 1 ? 0 : currentItemIndex + 1;
      }
    }

    if (!isEndOfList) {
      this.currentFocusedValue = this.options[nextItemIndex].value;
      const nextItemElem = this.stateHtmlElements[nextItemIndex];
      this.focusListItem(nextItemElem);
    }
  }

  private focusListItem(elem: HTMLElement): void {
    elem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    elem.focus();
  }
}
